import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Heart, Video, Sparkles, ShieldCheck, Smartphone, Clapperboard,
  Music2, Clock, Wand2, Link2, Quote, ArrowRight, Cake, Flower2, Gift, Briefcase, Check, Crown, Building2, Film,
} from "lucide-react";
import { Logo } from "@/components/Common";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DEMO_SLUG = "demomem1";

const reveal = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const HERO_WASH =
  "radial-gradient(1200px 600px at 20% 0%, hsla(35,55%,92%,0.9), transparent 60%), radial-gradient(900px 500px at 90% 5%, hsla(346,45%,92%,0.55), transparent 55%), radial-gradient(800px 500px at 50% 100%, hsla(196,55%,92%,0.5), transparent 60%)";

const STEPS = [
  { icon: Link2, t: "Create your event & get a link", d: "Set up a tribute in minutes — choose the occasion, add a photo and a few words, and copy your private link." },
  { icon: Video, t: "Everyone records from any device", d: "Friends and family tap the link and record a heartfelt video right in their browser. No app. No sign-up." },
  { icon: Clapperboard, t: "We weave it into one beautiful video", d: "Title cards, name captions, gentle crossfades and music — one polished film, ready for the big moment." },
];

const USE_CASES = [
  { icon: Cake, key: "birthday", title: "Birthdays", accent: "196 62% 38%", soft: "196 55% 92%",
    copy: "Turn a milestone birthday into a room full of voices — funny, tender, and unmistakably them.",
    img: "https://images.pexels.com/photos/8087555/pexels-photo-8087555.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { icon: Gift, key: "wedding", title: "Weddings & anniversaries", accent: "346 45% 46%", soft: "346 55% 93%",
    copy: "Gather blessings from everyone who couldn't make the toast — and a keepsake you'll want to relive every year.",
    img: "https://images.pexels.com/photos/5646493/pexels-photo-5646493.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { icon: Flower2, key: "memorial", title: "Memorials & celebrations of life", accent: "35 38% 45%", soft: "35 55% 92%",
    copy: "When words are hard to find, memories speak. Gather them, gently — a calm, private space for family and friends to share what someone meant to them.",
    img: "https://images.pexels.com/photos/8871516/pexels-photo-8871516.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", tender: true },
  { icon: Briefcase, key: "farewell", title: "Retirements & farewells", accent: "152 32% 32%", soft: "152 30% 92%",
    copy: "Send them off with decades of gratitude, gathered from every corner of their story.",
    img: "https://images.unsplash.com/photo-1541701773-52b1011cd501?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=940" },
];

const FEATURES = [
  { icon: Smartphone, t: "No app to install", d: "Works right in the browser." },
  { icon: Sparkles, t: "Works on any phone", d: "iPhone, Android, laptop — all welcome." },
  { icon: Wand2, t: "Automatic editing", d: "We handle the cuts, captions and pacing." },
  { icon: Music2, t: "Music & titles included", d: "Elegant title cards and a gentle score." },
  { icon: ShieldCheck, t: "Private & secure", d: "Unlisted links, optional PIN, your control." },
  { icon: Clock, t: "Ready in hours, not weeks", d: "Compile the final film whenever you're ready." },
];

const TESTIMONIALS = [
  { q: "We were scattered across three time zones for Mom's 80th. GatherVoices let all twenty-two of us send a message. Watching her face when it played — I'll never forget it.", n: "Priya N.", r: "Daughter · 80th birthday" },
  { q: "I set it up in ten minutes and dropped the link in the wedding group chat. The final film felt like a real keepsake, not a slideshow.", n: "Marcus & Dylan", r: "Newlyweds" },
  { q: "After Dad passed, this gave our family something gentle to do together. Cousins who couldn't travel still got to say goodbye.", n: "Hannah R.", r: "In memory of her father" },
];

const PRICING = [
  { key: "gather", name: "Gather", price: "Free", tag: "",
    features: ["Up to 15 tributes", "60-second clips", "One film + one recompile", "Kept for 14 days", "GatherVoices watermark"] },
  { key: "keepsake", name: "Keepsake", price: "$29", unit: "one-time", tag: "Most loved", featured: true,
    features: ["Unlimited tributes", "3-minute clips", "No watermark", "Kept forever", "Private family watch page", "Unlimited recompiles + clip downloads"] },
  { key: "legacy", name: "Legacy", price: "$59", unit: "one-time", tag: "",
    features: ["Everything in Keepsake", "Custom intro & thank-you cards", "Highlight + full versions", "Priority compilation", "Keepsake guest book PDF"] },
  { key: "pro", name: "Pro", price: "$19", unit: "/month", tag: "For teams",
    features: ["Unlimited Keepsake-level events", "Custom branding & logo", "Contributor CSV export", "For funeral homes, planners & schools"] },
];


const FAQS = [
  { q: "Is it really free?", a: "Yes — creating an event, collecting tributes, and your first film are free. Larger events and extra films will be part of our upcoming Pro plan." },
  { q: "How does the AI help me?", a: "GatherVoices drafts short scripts, page copy, and messages from a few of your answers — you always edit and decide. Your words are never used to train AI models, and nothing is ever sent for you." },
  { q: "How long are videos stored?", a: "Your tributes and finished film stay available for at least 12 months, and you can download everything to keep forever." },
  { q: "Do contributors need an account?", a: "Never. They tap your link, record from any phone or laptop, and they're done — no app, no sign-up." },
  { q: "Can I choose the order?", a: "Absolutely. Drag your tributes into any order — or hide a few — before we weave them together." },
  { q: "Is it private?", a: "Yes. Events are unlisted, you can add a PIN, and you decide whether the final watch page is shareable or kept completely private." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filmUrl, setFilmUrl] = useState(null);

  const go = () => navigate(user ? "/dashboard" : "/signup");

  useEffect(() => {
    api.get(`/watch/${DEMO_SLUG}`).then((r) => {
      if (r.data?.state === "ready" && r.data.film?.file_id) setFilmUrl(fileUrl(r.data.film.file_id));
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      {/* Nav */}
      <header className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
        <Logo />
        <nav className="flex items-center gap-2 sm:gap-3">
          <a href="#stories" data-testid="nav-stories-link" className="hidden sm:block text-sm font-medium px-4 py-2 rounded-xl hover:bg-[hsl(var(--muted))] transition-colors">Stories</a>
          <a href="#pricing" data-testid="nav-pricing-link" className="hidden sm:block text-sm font-medium px-4 py-2 rounded-xl hover:bg-[hsl(var(--muted))] transition-colors">Pricing</a>
          <Link to="/login" data-testid="nav-login-link" className="text-sm font-medium px-4 py-2 rounded-xl hover:bg-[hsl(var(--muted))] transition-colors">Sign in</Link>
          <button data-testid="nav-create-button" onClick={go} className="text-sm font-semibold px-4 py-2 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90 transition-opacity">Get started</button>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10" style={{ backgroundImage: HERO_WASH }} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-16 sm:pt-14 sm:pb-24 md:grid md:grid-cols-12 md:gap-10 items-center">
          <motion.div className="md:col-span-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-[hsl(var(--accent))]">
              <Heart size={14} /> One shared link · one lasting film
            </span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-[4rem] font-semibold tracking-[-0.02em] leading-[1.03]">
              Every voice.<br />One story.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[hsl(var(--muted-foreground))] max-w-md leading-relaxed">
              Collect video tributes from everyone who loves them — and turn them into one unforgettable film for birthdays, weddings, and celebrations of life.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="marketing-hero-create-tribute-button" onClick={go}
                className="px-6 py-3.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold shadow-[0_10px_22px_rgba(20,16,12,0.12)] hover:opacity-90 transition-opacity active:translate-y-[1px]">
                Create a tribute — free
              </button>
              <Link to={`/watch/${DEMO_SLUG}`} data-testid="marketing-see-example-link"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] font-semibold hover:bg-[hsl(var(--muted))] transition-colors">
                See an example <ArrowRight size={16} />
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[hsl(var(--muted-foreground))]">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={16} /> Private by default</span>
              <span className="inline-flex items-center gap-1.5"><Smartphone size={16} /> Works on any phone</span>
              <span className="inline-flex items-center gap-1.5"><Sparkles size={16} /> No app to install</span>
            </div>
          </motion.div>

          {/* Device mockup */}
          <motion.div className="md:col-span-6 mt-12 md:mt-0" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <DeviceMockup filmUrl={filmUrl} />
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <motion.div {...reveal} className="max-w-2xl">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold">How GatherVoices works</h2>
          <p className="mt-3 text-[hsl(var(--muted-foreground))]">Three gentle steps between an idea and a film everyone will remember.</p>
        </motion.div>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <motion.div key={i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }}
              className="relative rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-6 shadow-[0_1px_0_rgba(20,16,12,0.06),0_12px_30px_rgba(20,16,12,0.05)]">
              <div className="flex items-center justify-between">
                <div className="h-11 w-11 grid place-items-center rounded-xl bg-[hsl(35_55%_92%)] text-[hsl(var(--accent))]"><s.icon size={20} /></div>
                <span className="font-display text-3xl text-[hsl(var(--border))]">{i + 1}</span>
              </div>
              <h3 className="mt-4 font-display text-xl font-semibold">{s.t}</h3>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Use cases */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-6 sm:pb-10">
        <motion.h2 {...reveal} className="font-display text-3xl sm:text-4xl font-semibold">For every kind of moment</motion.h2>
        <div className="mt-8 grid gap-6 sm:gap-8">
          {USE_CASES.map((u, i) => (
            <motion.div key={u.key} {...reveal} data-testid={`usecase-${u.key}`}
              className={`grid md:grid-cols-2 gap-5 sm:gap-8 items-center rounded-3xl overflow-hidden border border-[hsl(var(--border))] bg-[hsl(var(--card))] ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
              <div className="aspect-[16/10] md:aspect-auto md:h-full min-h-[220px]">
                <img src={u.img} alt={u.title} loading="lazy" className="h-full w-full object-cover" />
              </div>
              <div className="p-6 sm:p-8">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `hsl(${u.soft})`, color: `hsl(${u.accent})` }}><u.icon size={20} /></div>
                <h3 className="mt-4 font-display text-2xl font-semibold">{u.title}</h3>
                <p className={`mt-3 leading-relaxed ${u.tender ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--muted-foreground))]"}`}>{u.copy}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* AI helps you find the words */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <motion.div {...reveal} data-testid="landing-ai-band"
          className="rounded-3xl overflow-hidden border border-[hsl(var(--border))] bg-gradient-to-br from-[hsl(35_55%_96%)] via-[hsl(var(--card))] to-[hsl(346_55%_96%)]">
          <div className="grid md:grid-cols-12 gap-5 sm:gap-10 items-center p-6 sm:p-10">
            <div className="md:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-[hsl(var(--accent))]">
                <Sparkles size={14} /> New · AI that helps you find the words
              </span>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-[-0.01em]">
                For the moments when words are hardest.
              </h2>
              <p className="mt-4 text-[hsl(var(--muted-foreground))] leading-relaxed max-w-xl">
                Grief makes people freeze. Joy makes people ramble. Our gentle
                copilot drafts a short script from a few of your answers — you edit,
                you decide, you record. Nothing is ever sent for you.
              </p>

              <ul className="mt-6 space-y-3">
                {[
                  { t: "Contributor coach", d: "Two or three questions in — a heartfelt 30 to 60 second draft comes back." },
                  { t: "Story starter", d: "A 60-second mini-interview drafts the tribute page paragraph in three tones." },
                  { t: "Message studio", d: "One-tap invites, reminders, and thank-yous that sound like you wrote them." },
                  { t: "Eulogy draft (Keepsake)", d: "We gather every submitted memory into a heartfelt draft — for the family, in case it helps." },
                ].map((row) => (
                  <li key={row.t} className="flex items-start gap-3">
                    <span className="mt-0.5 h-5 w-5 grid place-items-center rounded-full bg-[hsl(var(--accent))] text-white shrink-0"><Check size={12} /></span>
                    <div>
                      <div className="font-semibold text-sm">{row.t}</div>
                      <div className="text-sm text-[hsl(var(--muted-foreground))]">{row.d}</div>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button data-testid="landing-ai-create-button" onClick={go}
                  className="px-5 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold">
                  Try it with your event
                </button>
                <span className="text-xs text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1.5">
                  <ShieldCheck size={13} /> Your words are never used to train AI models.
                </span>
              </div>
            </div>

            {/* Mock copilot card */}
            <div className="md:col-span-5">
              <div className="rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-5 shadow-[0_20px_50px_rgba(20,16,12,0.08)]">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 grid place-items-center rounded-xl" style={{ background: "hsl(35 55% 92%)", color: "hsl(35 60% 30%)" }}>
                    <Sparkles size={18} />
                  </div>
                  <div className="text-sm font-semibold">Help me find the words</div>
                </div>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="rounded-xl bg-[hsl(var(--muted))] px-3 py-2 text-[hsl(var(--muted-foreground))]">
                    <span className="font-semibold text-[hsl(var(--foreground))]">Q: </span>How did you know them?
                  </div>
                  <div className="rounded-xl border border-[hsl(var(--border))] px-3 py-2">She was my grandmother — the one who taught me to bake pies.</div>
                  <div className="rounded-xl bg-[hsl(var(--muted))] px-3 py-2 text-[hsl(var(--muted-foreground))]">
                    <span className="font-semibold text-[hsl(var(--foreground))]">Q: </span>One memory that always comes back?
                  </div>
                  <div className="rounded-xl border border-[hsl(var(--border))] px-3 py-2">Sunday afternoons on her porch, reading me stories.</div>
                </div>
                <div className="mt-5 rounded-xl border-2 border-dashed border-[hsl(var(--border))] p-4">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5 inline-flex items-center gap-1">
                    <Sparkles size={11} className="text-[hsl(var(--accent))]" /> Drafted for you — edit anything
                  </div>
                  <p className="text-sm leading-relaxed">
                    Hi, I&apos;m Priya. Eleanor was my grandmother, and the one who taught
                    me that flour on your cheek isn&apos;t a mistake — it&apos;s a memory.
                    I&apos;ll never forget her porch on Sunday afternoons, reading me
                    stories that always ended with pie. To her family: thank you for
                    sharing her with all of us.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Feature grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <motion.h2 {...reveal} className="font-display text-3xl sm:text-4xl font-semibold text-center">Everything&apos;s handled for you</motion.h2>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div key={i} {...reveal} transition={{ ...reveal.transition, delay: (i % 3) * 0.06 }}
              className="flex items-start gap-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
              <div className="h-11 w-11 shrink-0 grid place-items-center rounded-xl bg-[hsl(35_55%_92%)] text-[hsl(var(--accent))]"><f.icon size={20} /></div>
              <div>
                <h3 className="font-semibold">{f.t}</h3>
                <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{f.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20">
        <motion.h2 {...reveal} className="font-display text-3xl sm:text-4xl font-semibold">Moments families keep</motion.h2>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure key={i} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }}
              className="flex flex-col rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 shadow-[0_1px_0_rgba(20,16,12,0.06),0_12px_30px_rgba(20,16,12,0.05)]">
              <Quote size={22} className="text-[hsl(var(--accent))]" />
              <blockquote className="mt-3 leading-relaxed flex-1">{t.q}</blockquote>
              <figcaption className="mt-5 pt-4 border-t border-[hsl(var(--border))]">
                <div className="font-semibold">{t.n}</div>
                <div className="text-sm text-[hsl(var(--muted-foreground))]">{t.r}</div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </section>

      {/* Keepsake Story — second product line */}
      <section id="stories" className="scroll-mt-20 relative overflow-hidden">
        <div className="bg-[hsl(var(--letterbox))] text-[hsl(var(--letterbox-foreground))]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/50">New · Keepsake Story</span>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold leading-tight">Their whole story.<br />Beautifully told.</h2>
                <p className="mt-4 text-lg text-white/70 leading-relaxed">Legacy films used to take a film crew and five figures. Now they take a lifetime of stories and an afternoon. A gentle AI interviewer asks the right questions — your family records from home, and we edit it into a real documentary.</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button data-testid="landing-create-story-button" onClick={() => navigate(user ? "/stories/new" : "/signup")}
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] font-semibold hover:opacity-90 transition-opacity"><Clapperboard size={18} /> Create a Keepsake Story</button>
                  <a href="#pricing" data-testid="landing-gift-story-link" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors"><Gift size={18} /> Give someone their story</a>
                </div>
              </div>
              <div className="rounded-2xl border border-white/15 bg-white/5 p-6 sm:p-8">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="rounded-xl bg-white/5 p-5"><div className="font-display text-3xl font-semibold">$25,000+</div><div className="mt-1 text-xs text-white/50 uppercase tracking-wide">Boutique film crew</div></div>
                  <div className="rounded-xl bg-[hsl(var(--accent))]/20 p-5"><div className="font-display text-3xl font-semibold">$99</div><div className="mt-1 text-xs text-white/60 uppercase tracking-wide">GatherVoices, from home</div></div>
                  <div className="rounded-xl bg-white/5 p-5"><div className="font-display text-3xl font-semibold">3 months</div><div className="mt-1 text-xs text-white/50 uppercase tracking-wide">Traditional production</div></div>
                  <div className="rounded-xl bg-[hsl(var(--accent))]/20 p-5"><div className="font-display text-3xl font-semibold">Days</div><div className="mt-1 text-xs text-white/60 uppercase tracking-wide">Self-serve, resumable</div></div>
                </div>
                <p className="mt-5 text-sm text-white/60 text-center">Solo · Couple · Chorus — a whole family remembered in one film.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20 pt-16 sm:pt-20 scroll-mt-20">
        <motion.div {...reveal} className="text-center max-w-2xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold">Simple, gentle pricing</h2>
          <p className="mt-3 text-[hsl(var(--muted-foreground))]">Two ways to honor someone — a tribute for a moment, or a documentary of a whole life.</p>
        </motion.div>

        {/* Keepsake Story tiers */}
        <div className="mt-10">
          <h3 className="font-mono text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] flex items-center gap-2"><Clapperboard size={14} /> Keepsake Stories</h3>
          <div className="mt-4 grid gap-5 md:grid-cols-2 lg:grid-cols-2 max-w-3xl">
            {[
              { key: "story", name: "Story", price: "$99", tag: "One-time", features: ["Solo or Couple life story", "Up to 3 interview hours", "10-minute film + AI narration", "Memory Book PDF", "2 revision rounds · stored forever"] },
              { key: "story_premium", name: "Story Premium", price: "$249", tag: "Best for families", featured: true, features: ["Everything in Story, plus Chorus", "Unlimited interviews & archive", "Up to 30-minute film", "Photo restoration & priority rendering", "5 revision rounds · gift packaging"] },
            ].map((p) => (
              <div key={p.key} data-testid={`pricing-${p.key}`} className={`relative flex flex-col rounded-2xl border p-6 bg-[hsl(var(--card))] ${p.featured ? "border-[hsl(var(--accent))] shadow-[var(--film-shadow)]" : "border-[hsl(var(--film-border))]"}`}>
                {p.tag && <span className={`absolute -top-3 left-6 text-xs font-semibold px-3 py-1 rounded-full ${p.featured ? "bg-[hsl(var(--accent))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{p.tag}</span>}
                <div className="font-display text-xl font-semibold">{p.name}</div>
                <div className="mt-2 font-display text-4xl font-semibold">{p.price}</div>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {p.features.map((f, j) => <li key={j} className="flex items-start gap-2.5 text-sm"><span className="mt-0.5 text-[hsl(var(--accent))]"><Check size={16} /></span><span>{f}</span></li>)}
                </ul>
                <button data-testid={`pricing-cta-${p.key}`} onClick={() => navigate(user ? "/stories/new" : "/signup")}
                  className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity ${p.featured ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "border border-[hsl(var(--border))]"}`}>
                  <Clapperboard size={16} /> Start a {p.name}
                </button>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">The first interview chapter is free — record it and get a 60-second teaser before you decide.</p>
        </div>

        {/* Event tribute tiers */}
        <div className="mt-12">
          <h3 className="font-mono text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] flex items-center gap-2"><Film size={14} /> Event Tributes</h3>
        <div className="mt-4 grid gap-5 md:grid-cols-2 lg:grid-cols-4 items-stretch">
          {PRICING.map((p, i) => (
            <motion.div key={p.key} {...reveal} transition={{ ...reveal.transition, delay: (i % 4) * 0.06 }} data-testid={`pricing-${p.key}`}
              className={`relative flex flex-col rounded-2xl border p-6 bg-[hsl(var(--card))] ${p.featured ? "border-[hsl(var(--accent))] shadow-[0_18px_45px_rgba(20,16,12,0.10)]" : "border-[hsl(var(--border))]"}`}>
              {p.tag && (
                <span className={`absolute -top-3 left-6 text-xs font-semibold px-3 py-1 rounded-full ${p.featured ? "bg-[hsl(var(--accent))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{p.tag}</span>
              )}
              <div className="font-display text-xl font-semibold">{p.name}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-display text-4xl font-semibold">{p.price}</span>
                {p.unit && <span className="text-sm text-[hsl(var(--muted-foreground))]">{p.unit}</span>}
              </div>
              <ul className="mt-5 space-y-2.5 flex-1">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-sm">
                    <span className="mt-0.5 text-[hsl(var(--accent))]"><Check size={16} /></span><span>{f}</span>
                  </li>
                ))}
              </ul>
              <button data-testid={`pricing-cta-${p.key}`} onClick={go}
                className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold transition-opacity hover:opacity-90 ${p.featured || p.key === "pro" ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "border border-[hsl(var(--border))]"}`}>
                {p.key === "gather" ? "Start free" : p.key === "pro" ? <><Crown size={16} /> Start Pro</> : `Choose ${p.name}`}
              </button>
            </motion.div>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-[hsl(var(--muted-foreground))]">Keepsake & Legacy are one-time, per event. Pro is billed monthly and covers unlimited events.</p>
        </div>
      </section>

      {/* B2B — funeral homes & event planners */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20">
        <motion.div {...reveal} className="relative overflow-hidden rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 sm:p-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-[hsl(var(--accent))]"><Building2 size={14} /> GatherVoices Pro</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold">For funeral homes & event planners</h2>
            <p className="mt-3 text-[hsl(var(--muted-foreground))] leading-relaxed max-w-md">Offer families a beautiful tribute film with your own name on it. One subscription, unlimited Keepsake-level events, and the exports your team needs.</p>
            <ul className="mt-5 space-y-2.5">
              {["Your logo & name on every tribute and watch page", "Unlimited Keepsake-level events", "Contributor CSV export for your records", "A repeatable, dignified workflow for every family"].map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm"><span className="mt-0.5 text-[hsl(var(--accent))]"><Check size={16} /></span><span>{f}</span></li>
              ))}
            </ul>
            <button data-testid="b2b-start-pro-button" onClick={go} className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity"><Crown size={16} /> Start GatherVoices Pro — $19/mo</button>
          </div>
          <div className="rounded-2xl overflow-hidden aspect-[4/3]">
            <img src="https://images.pexels.com/photos/8871516/pexels-photo-8871516.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=800" alt="A dignified memorial setting" loading="lazy" className="h-full w-full object-cover" />
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <motion.h2 {...reveal} className="font-display text-3xl sm:text-4xl font-semibold text-center">Questions, answered</motion.h2>
        <motion.div {...reveal} className="mt-8">
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}
                className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-5">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[hsl(var(--muted-foreground))] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <motion.div {...reveal} className="relative overflow-hidden rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 sm:p-12 text-center">
          <div className="absolute inset-0 -z-10" style={{ backgroundImage: HERO_WASH }} />
          <h2 className="font-display text-3xl sm:text-4xl font-semibold">Start gathering voices today</h2>
          <p className="mt-3 text-[hsl(var(--muted-foreground))] max-w-lg mx-auto">Create your event, share one link, and let the people who matter speak from the heart.</p>
          <button data-testid="marketing-footer-cta-button" onClick={go}
            className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold shadow-[0_10px_22px_rgba(20,16,12,0.12)] hover:opacity-90 transition-opacity active:translate-y-[1px]">
            <Heart size={16} /> Create a tribute — free
          </button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[hsl(var(--border))]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid gap-6 sm:grid-cols-2 items-center">
          <div>
            <Logo />
            <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))] max-w-xs">Every voice. One story. Collaborative video tributes for life&apos;s biggest moments.</p>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 sm:justify-end text-sm">
            <a data-testid="footer-privacy-link" href="mailto:support@gathervoices.io?subject=Privacy" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">Privacy</a>
            <a data-testid="footer-terms-link" href="mailto:support@gathervoices.io?subject=Terms" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">Terms</a>
            <a data-testid="footer-contact-link" href="mailto:support@gathervoices.io" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">Contact</a>
          </nav>
        </div>
        <div className="border-t border-[hsl(var(--border))]">
          <p className="max-w-6xl mx-auto px-4 sm:px-6 py-5 text-xs text-[hsl(var(--muted-foreground))]">© {new Date().getFullYear()} GatherVoices · Made with care</p>
        </div>
      </footer>
    </div>
  );
}

function DeviceMockup({ filmUrl }) {
  return (
    <div className="relative mx-auto max-w-[520px]">
      {/* Laptop */}
      <div className="rounded-t-2xl border-[6px] border-[hsl(24_12%_20%)] bg-[hsl(24_12%_20%)] shadow-[0_30px_70px_rgba(20,16,12,0.25)]">
        <div className="rounded-t-xl overflow-hidden bg-black aspect-video">
          {filmUrl ? (
            <video src={filmUrl} autoPlay muted loop playsInline poster="https://images.pexels.com/photos/10302634/pexels-photo-10302634.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" className="h-full w-full object-cover" data-testid="hero-mockup-video" />
          ) : (
            <img src="https://images.pexels.com/photos/10302634/pexels-photo-10302634.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Tribute preview" className="h-full w-full object-cover" />
          )}
        </div>
      </div>
      <div className="h-3 rounded-b-xl bg-[hsl(24_10%_28%)] shadow-[0_10px_20px_rgba(20,16,12,0.2)]" />
      <div className="mx-auto h-1.5 w-1/3 rounded-b-lg bg-[hsl(24_10%_22%)]" />

      {/* Phone overlapping */}
      <div className="absolute -bottom-6 -right-2 sm:-right-6 w-24 sm:w-28 rotate-[6deg]">
        <div className="rounded-[1.6rem] border-[5px] border-[hsl(24_12%_20%)] bg-[hsl(24_12%_20%)] shadow-[0_18px_40px_rgba(20,16,12,0.28)]">
          <div className="rounded-[1.2rem] overflow-hidden bg-black aspect-[9/16]">
            {filmUrl ? (
              <video src={filmUrl} autoPlay muted loop playsInline className="h-full w-full object-cover" />
            ) : (
              <img src="https://images.unsplash.com/photo-1541701773-52b1011cd501?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=500" alt="Tribute preview" className="h-full w-full object-cover" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
